import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';

// Wide aspect (~8.8:1) so the chart doesn't balloon vertically on wide containers
// where the SVG scales by aspect ratio (width 100%)
const W = 880, H = 100;
const PAD = { top: 10, right: 60, bottom: 18, left: 64 }; // left padded to fit "8.0 sat/vB" label

function fmtDate(ts) {
  if (ts == null) return '—';
  const d = new Date(ts * 1000);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

const PRESETS = [
  { key: '1w',  label: '1W',  days: 7 },
  { key: '2w',  label: '2W',  days: 14 },
  { key: '1m',  label: '1M',  days: 30 },
  { key: '3m',  label: '3M',  days: 90 },
  { key: 'all', label: 'ALL', days: null },
];

const TABULAR = { fontVariantNumeric: 'tabular-nums' };

function levelColor(level) {
  switch (level) {
    case 'HIGH': return '#ff4d4d';
    case 'MED':  return '#f7c948';
    case 'LOW':  return '#26c49a';
    default:     return '#888';
  }
}

export default function CongestionChart({ data, unit = 'sat/vB', chainStates }) {
  // ── All hooks first ────────────────────────────────────────────────────────
  const series   = (data && data.series)  || [];
  const peaks    = (data && data.peaks)   || [];
  const troughs  = (data && data.troughs) || [];
  const p25      = data?.p25;
  const p75      = data?.p75;
  const tMinData = series[0]?.ts;
  const tMaxData = series[series.length - 1]?.ts;

  const [view, setView] = useState({ min: tMinData, max: tMaxData });
  const dragRef = useRef({ active: false, startX: 0, startView: null });
  const svgRef  = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Reset view if underlying data range changes — default to LAST 1W
  useEffect(() => {
    if (tMinData != null && tMaxData != null) {
      const oneWeek = 7 * 86400;
      const min = Math.max(tMinData, tMaxData - oneWeek);
      setView({ min, max: tMaxData });
    }
  }, [tMinData, tMaxData]);

  // Filtered series within visible window
  const vSeries = useMemo(
    () => series.filter(s => view.min != null && s.ts >= view.min && s.ts <= view.max),
    [series, view.min, view.max]
  );
  const vPeaks   = useMemo(() => peaks.filter(p => view.min != null && p.ts >= view.min && p.ts <= view.max),  [peaks, view.min, view.max]);
  const vTroughs = useMemo(() => troughs.filter(t => view.min != null && t.ts >= view.min && t.ts <= view.max), [troughs, view.min, view.max]);

  // Pixel-to-timestamp helper
  const xToTs = useCallback((clientX) => {
    if (!svgRef.current || view.min == null) return view.min;
    const rect = svgRef.current.getBoundingClientRect();
    const x0 = PAD.left, x1 = W - PAD.right;
    const svgX = ((clientX - rect.left) / rect.width) * W;
    const span = view.max - view.min;
    const t = view.min + ((svgX - x0) / (x1 - x0)) * span;
    return Math.max(view.min, Math.min(view.max, t));
  }, [view.min, view.max]);

  // Wheel = zoom around cursor
  const handleWheel = useCallback((e) => {
    if (view.min == null || tMinData == null) return;
    e.preventDefault();
    const cursorTs = xToTs(e.clientX);
    const zoomFactor = Math.exp(e.deltaY * 0.001);
    const dataSpan = tMaxData - tMinData;
    let newSpan = (view.max - view.min) * zoomFactor;
    newSpan = Math.max(dataSpan * 0.005, Math.min(dataSpan, newSpan));
    const ratio = (cursorTs - view.min) / (view.max - view.min);
    let newMin = cursorTs - newSpan * ratio;
    let newMax = newMin + newSpan;
    if (newMin < tMinData) { newMin = tMinData; newMax = newMin + newSpan; }
    if (newMax > tMaxData) { newMax = tMaxData; newMin = newMax - newSpan; }
    setView({ min: newMin, max: newMax });
  }, [view.min, view.max, tMinData, tMaxData, xToTs]);

  // Drag = pan. Window-level listeners attached ONLY while dragging — released on mouseup.
  // Avoids running a handler on every mousemove globally (which was starving BlockClock hover).
  const handleMouseDown = useCallback((e) => {
    if (view.min == null) return;
    dragRef.current = { active: true, startX: e.clientX, startView: { ...view } };
    setIsDragging(true);

    const onMove = (ev) => {
      if (!dragRef.current.active || !svgRef.current || tMinData == null) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x0p = PAD.left, x1p = W - PAD.right;
      const dxPx = ev.clientX - dragRef.current.startX;
      const startSpan = dragRef.current.startView.max - dragRef.current.startView.min;
      const dxTs = -(dxPx / rect.width) * W * startSpan / (x1p - x0p);
      let newMin = dragRef.current.startView.min + dxTs;
      let newMax = dragRef.current.startView.max + dxTs;
      const span = newMax - newMin;
      if (newMin < tMinData) { newMin = tMinData; newMax = newMin + span; }
      if (newMax > tMaxData) { newMax = tMaxData; newMin = newMax - span; }
      setView({ min: newMin, max: newMax });
    };
    const onUp = () => {
      dragRef.current.active = false;
      setIsDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [view, tMinData, tMaxData]);

  // Wheel listener stays on SVG only — fires when over the chart, doesn't affect other elements
  useEffect(() => {
    const node = svgRef.current;
    if (!node) return;
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const setPreset = useCallback((preset) => {
    if (tMinData == null || tMaxData == null) return;
    if (preset.days == null) {
      setView({ min: tMinData, max: tMaxData });
    } else {
      const span = preset.days * 86400;
      let newMin = tMaxData - span;
      if (newMin < tMinData) newMin = tMinData;
      setView({ min: newMin, max: tMaxData });
    }
  }, [tMinData, tMaxData]);

  // ── Early return AFTER all hooks ──────────────────────────────────────────
  if (!data || series.length === 0 || view.min == null) return null;

  // ── Computed render values ────────────────────────────────────────────────
  const x0 = PAD.left, x1 = W - PAD.right;
  const y0 = H - PAD.bottom, y1 = PAD.top;
  const tSpan = view.max - view.min || 1;

  const vMin = 0;
  const vMaxRaw = vSeries.length > 0
    ? Math.max(...vSeries.map(s => s.value), p75 || 0)
    : (p75 || 1);
  const vMax = vMaxRaw * 1.15 || 1;

  const xOf = (ts) => x0 + ((ts - view.min) / tSpan) * (x1 - x0);
  const yOf = (v)  => y0 - ((v - vMin) / (vMax - vMin)) * (y0 - y1);

  const linePath = vSeries.map((s, i) =>
    `${i === 0 ? 'M' : 'L'} ${xOf(s.ts).toFixed(1)} ${yOf(s.value).toFixed(1)}`
  ).join(' ');

  const yTicks = [vMin, vMax * 0.33, vMax * 0.66, vMax].map(v => ({ v, y: yOf(v) }));
  const xTickN = 6;
  const xTicks = Array.from({ length: xTickN + 1 }, (_, i) => {
    const ts = view.min + (i / xTickN) * tSpan;
    return { ts, x: xOf(ts) };
  });

  const isPresetActive = (preset) => {
    if (tMinData == null || tMaxData == null) return false;
    if (preset.days == null) return view.min <= tMinData + 60 && view.max >= tMaxData - 60;
    const presetSpan = preset.days * 86400;
    return Math.abs(tSpan - presetSpan) / presetSpan < 0.05 && view.max >= tMaxData - 86400;
  };

  // Static chain status chips (informational only — no tab behavior, chart below is BTC-only)
  const chains = ['btc', 'eth'].filter(k => chainStates && chainStates[k]);

  return (
    <div>
      {chains.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 6,
          padding: '0 0 8px',
          fontFamily: 'Orbitron, sans-serif',
        }}>
          {chains.map(ck => {
            const c = chainStates[ck];
            const col = levelColor(c.level);
            const bgTint = `${col}14`;
            return (
              <div
                key={ck}
                style={{
                  flex: 1,
                  background: bgTint,
                  border: `1px solid ${col}`,
                  borderRadius: 2,
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 700, color: '#ddd', letterSpacing: '1.5px' }}>
                  {ck.toUpperCase()}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: col, letterSpacing: '1.5px' }}>
                  {c.level}
                </span>
                <span style={{ fontSize: 9, color: '#bbb', letterSpacing: '0.5px', marginLeft: 'auto' }}>
                  {c.current.toFixed(2)} {c.unit}
                </span>
                <span style={{ fontSize: 7, color: '#666', letterSpacing: '0.8px' }}>
                  p30d {Math.round(c.pct_30d * 100)} · p_all {Math.round(c.pct_all * 100)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Range presets — compact strip, NO padding, tiny font */}
      <div className="cc-presets" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '0 0 4px 0',
        position: 'relative',
        zIndex: 5,
        height: '14px',
        lineHeight: '14px',
      }}>
        <span style={{
          fontSize: '9px', color: '#ddd', letterSpacing: '1.5px', fontWeight: 700,
          fontFamily: 'Orbitron, sans-serif', marginRight: '12px',
        }}>
          BTC MEMPOOL FEE
        </span>
        {PRESETS.map(p => {
          const active = isPresetActive(p);
          return (
            <button
              key={p.key}
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreset(p); }}
              style={{
                background: active ? 'rgba(55,230,207,0.12)' : 'transparent',
                color: active ? '#37E6CF' : '#777',
                border: 'none',
                borderBottom: active ? '1px solid #37E6CF' : '1px solid transparent',
                borderRadius: 0,
                margin: 0,
                padding: '0 4px',
                height: '14px',
                lineHeight: '14px',
                fontFamily: 'Orbitron, sans-serif',
                fontSize: '8px',
                fontWeight: 700,
                letterSpacing: '0.8px',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: 'none',
              }}
            >{p.label}</button>
          );
        })}
        <span style={{ flex: 1 }} />
        <span style={{
          fontSize: '7px', color: '#555', letterSpacing: '0.5px',
          fontFamily: 'Orbitron, sans-serif',
        }}>
          {fmtDate(view.min)} → {fmtDate(view.max)}
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', display: 'block', cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
        onMouseDown={handleMouseDown}
      >
        {/* Threshold band */}
        {p25 != null && p75 != null && (
          <rect
            x={x0} y={yOf(p75)}
            width={x1 - x0} height={Math.max(0, yOf(p25) - yOf(p75))}
            fill="rgba(247, 201, 72, 0.04)"
          />
        )}

        {/* Y gridlines */}
        {yTicks.map((t, i) => (
          <line key={`yg-${i}`} x1={x0} x2={x1} y1={t.y} y2={t.y}
            stroke="#1a1a1a" strokeWidth="1" strokeDasharray="2 4" />
        ))}

        {/* Threshold lines */}
        {p75 != null && (
          <line x1={x0} x2={x1} y1={yOf(p75)} y2={yOf(p75)}
            stroke="#ff4d4d" strokeWidth="1" strokeDasharray="4 3" opacity="0.7" />
        )}
        {p25 != null && (
          <line x1={x0} x2={x1} y1={yOf(p25)} y2={yOf(p25)}
            stroke="#26c49a" strokeWidth="1" strokeDasharray="4 3" opacity="0.7" />
        )}

        {/* Threshold labels (right side) */}
        {p75 != null && (
          <text x={x1 + 6} y={yOf(p75) + 3} fill="#ff4d4d" fontFamily="Orbitron" fontWeight="700" fontSize="8" letterSpacing="0.8">
            HIGH · {p75.toFixed(2)}
          </text>
        )}
        {p25 != null && (
          <text x={x1 + 6} y={yOf(p25) + 3} fill="#26c49a" fontFamily="Orbitron" fontWeight="700" fontSize="8" letterSpacing="0.8">
            LOW · {p25.toFixed(2)}
          </text>
        )}

        {/* Series line */}
        <path d={linePath} fill="none" stroke="#37E6CF" strokeWidth="1.2" opacity="0.9" />

        {/* Peak markers (red) */}
        {vPeaks.map((p, i) => (
          <g key={`pk-${i}`}>
            <circle cx={xOf(p.ts)} cy={yOf(p.value)} r="3.5" fill="#ff4d4d" stroke="#0a0a0a" strokeWidth="1" />
            <text x={xOf(p.ts)} y={yOf(p.value) - 8} textAnchor="middle"
              fill="#ff4d4d" fontFamily="Orbitron" fontWeight="700" fontSize="8" style={TABULAR}>
              {p.value.toFixed(1)}
            </text>
          </g>
        ))}

        {/* Trough markers (green) */}
        {vTroughs.map((t, i) => (
          <g key={`tr-${i}`}>
            <circle cx={xOf(t.ts)} cy={yOf(t.value)} r="3.5" fill="#26c49a" stroke="#0a0a0a" strokeWidth="1" />
            <text x={xOf(t.ts)} y={yOf(t.value) + 14} textAnchor="middle"
              fill="#26c49a" fontFamily="Orbitron" fontWeight="700" fontSize="8" style={TABULAR}>
              {t.value.toFixed(1)}
            </text>
          </g>
        ))}

        {/* Y axis labels — unit appended to topmost tick only */}
        {yTicks.map((t, i) => {
          const isTop = i === yTicks.length - 1;
          const label = `${t.v.toFixed(t.v < 10 ? 1 : 0)}${isTop ? ` ${unit}` : ''}`;
          return (
            <text key={`yt-${i}`} x={x0 - 6} y={t.y + 3} textAnchor="end"
              fill={isTop ? '#888' : '#555'} fontFamily="Orbitron" fontSize="8" letterSpacing="0.5">
              {label}
            </text>
          );
        })}

        {/* X axis labels — first/last edge-anchored to avoid colliding with Y labels */}
        {xTicks.map((t, i) => {
          const isFirst = i === 0;
          const isLast = i === xTicks.length - 1;
          const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
          return (
            <text key={`xt-${i}`} x={t.x} y={H - 4} textAnchor={anchor}
              fill="#555" fontFamily="Orbitron" fontSize="8" letterSpacing="0.5">
              {fmtDate(t.ts)}
            </text>
          );
        })}

      </svg>
    </div>
  );
}
